import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      // Single-axis visual + semantic enum, shaped to match AlwaysAI's Badge so
      // its generic usages port unchanged. App-specific domain variants (e.g. a
      // rating spectrum) compose from these tokens at the app level.
      variant: {
        default: "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        outline: "border-border text-foreground",
        muted: "border-transparent bg-muted text-muted-foreground",
        neutral: "border-sand-200 bg-sand-100 text-bark",
        destructive: "border-transparent bg-destructive text-white shadow hover:bg-destructive/80",
        success: "border-success-soft bg-success-soft text-success",
        warning: "border-warning-soft bg-warning-soft text-warning",
        danger: "border-danger-soft bg-danger-soft text-danger",
        info: "border-info-soft bg-info-soft text-info",
        ai: "border-ai-light bg-ai-light text-ai",
      },
      size: {
        sm: "px-3 py-1 text-xs font-medium",
        md: "px-3 py-1 text-xs font-semibold",
        lg: "px-4 py-2 text-sm font-medium",
      },
    },
    defaultVariants: {
      // "default" follows the shadcn/AlwaysAI convention (was "neutral" before
      // the NO-213 treatment adoption).
      variant: "default",
      size: "sm",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}

export { badgeVariants };
